import React, { memo, useCallback, useMemo } from 'react';

import { Box, Switch, Typography } from '@mui/material';
import DataFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/DataFieldCDS';
import DateFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/DateFieldCDS';
import RequiredIcon from 'app/shared/componentsRestyled/coreDesignSystem/form/RequiredIcon';
import TextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/TextFieldCDS';
import PendingChangeWrapperCDS from 'app/shared/componentsRestyled/coreDesignSystem/PendingChangeWrapperCDS';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldInputType } from 'constants/fields';
import { PendingChangesActionType } from 'constants/pendingChanges';

import { CUSTOM_FIELDS_VALUE_TYPE } from '../../CustomFieldsSection.constants';
import {
  coerceBooleanFromString,
  coerceBooleanToString,
  isCalculated,
  validateValueFormat,
} from '../../CustomFieldsSection.utils';

import DescriptionTooltip from './components/DescriptionTooltip';
import FormatWarningTooltip from './components/FormatWarningTooltip';
import { PERCENTAGE_DECIMAL_SCALE, TEXT_FIELD_NUMBER_TYPE } from './CustomFieldInput.constants';
import {
  labelWithTooltipStyles,
  switchFieldErrorStyles,
  switchFieldRowStyles,
  switchFieldWrapperStyles,
} from './CustomFieldInput.styles';
import { resolveErrorMessage } from './CustomFieldInput.utils';

const buildLabelNode = ({ name, description, required, formatWarningMessage }) => {
  if (!description && !required && !formatWarningMessage) {
    return name;
  }

  return (
    <Box component="span" sx={labelWithTooltipStyles}>
      <Box component="span">{name}</Box>
      {required && <RequiredIcon />}
      {!!description && <DescriptionTooltip description={description} />}
      {!!formatWarningMessage && <FormatWarningTooltip message={formatWarningMessage} />}
    </Box>
  );
};

const CustomFieldInput = ({
  definition,
  value,
  errorKey,
  disabled,
  required,
  isHidden,
  pendingChangesActionType = null,
  onChange,
  onBlur,
  dataTestId,
}) => {
  const { id, name, valueType, description } = definition;
  const trimmedDescription = description?.trim();
  const errorMessage = resolveErrorMessage(errorKey);
  const hasError = !!errorMessage;
  const formatWarningMessage = useMemo(() => {
    if (!isCalculated(definition)) {
      return null;
    }

    return validateValueFormat({ valueType, value });
  }, [definition, valueType, value]);
  const labelNode = buildLabelNode({
    name,
    description: trimmedDescription,
    required,
    formatWarningMessage,
  });
  const isFieldChanged =
    !!pendingChangesActionType && pendingChangesActionType !== PendingChangesActionType.NO_CHANGES;
  const switchPendingChangesActionType =
    pendingChangesActionType ?? PendingChangesActionType.NO_CHANGES;

  const onTextChange = useCallback(
    event => {
      onChange(id, event.target.value ?? '');
    },
    [id, onChange],
  );

  const onDataFieldChange = useCallback(
    event => {
      const nextValue = event.target.value;
      onChange(id, nextValue == null || nextValue === '' ? '' : String(nextValue));
    },
    [id, onChange],
  );

  const onDateChange = useCallback(
    nextDate => {
      const isoValue = dayjs(nextDate).isValid() ? dayjs(nextDate).format('YYYY-MM-DD') : '';
      onChange(id, isoValue);
    },
    [id, onChange],
  );

  const onFieldBlur = useCallback(() => {
    onBlur?.(id);
  }, [id, onBlur]);

  const onSwitchChange = useCallback(
    event => {
      onChange(id, coerceBooleanToString(event.target.checked));
      onBlur?.(id);
    },
    [id, onBlur, onChange],
  );

  const dateValue = useMemo(() => (value ? dayjs(value) : null), [value]);

  if (valueType === CUSTOM_FIELDS_VALUE_TYPE.BOOLEAN) {
    if (isHidden) {
      return null;
    }

    return (
      <Box sx={switchFieldWrapperStyles}>
        <Box sx={switchFieldRowStyles}>
          <PendingChangeWrapperCDS
            isSmall
            pendingChangesActionType={switchPendingChangesActionType}
          >
            <Switch
              checked={coerceBooleanFromString(value)}
              data-testid={dataTestId}
              disabled={disabled}
              onChange={onSwitchChange}
            />
          </PendingChangeWrapperCDS>
          <Typography variant="regular14">
            {name}
            {required && <RequiredIcon />}
          </Typography>
          {!!trimmedDescription && <DescriptionTooltip description={trimmedDescription} />}
        </Box>
        {!!hasError && (
          <Typography sx={switchFieldErrorStyles} variant="regular12">
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  }

  if (valueType === CUSTOM_FIELDS_VALUE_TYPE.DATE) {
    return (
      <DateFieldCDS
        clearable
        dataTestId={dataTestId}
        disabled={disabled}
        error={hasError}
        helperText={errorMessage}
        isFieldChanged={isFieldChanged}
        isHidden={isHidden}
        label={labelNode}
        name={id}
        required={required}
        value={dateValue}
        withRequiredIcon={false}
        onBlur={onFieldBlur}
        onChange={onDateChange}
        onFocus={_.noop}
      />
    );
  }

  if (valueType === CUSTOM_FIELDS_VALUE_TYPE.PERCENTAGE) {
    return (
      <DataFieldCDS
        isNumericString
        dataTestId={dataTestId}
        decimalScale={PERCENTAGE_DECIMAL_SCALE}
        decimalSeparator="."
        disabled={disabled}
        error={hasError}
        helperText={errorMessage}
        isFieldChanged={isFieldChanged}
        isHidden={isHidden}
        label={labelNode}
        name={id}
        required={required}
        type={FieldInputType.PERCENTAGE_RESTYLED}
        value={value ?? ''}
        withRequiredIcon={false}
        onBlur={onFieldBlur}
        onChange={onDataFieldChange}
      />
    );
  }

  if (valueType === CUSTOM_FIELDS_VALUE_TYPE.CURRENCY) {
    return (
      <DataFieldCDS
        isNumericString
        dataTestId={dataTestId}
        disabled={disabled}
        error={hasError}
        helperText={errorMessage}
        isFieldChanged={isFieldChanged}
        isHidden={isHidden}
        label={labelNode}
        name={id}
        required={required}
        type={FieldInputType.CURRENCY}
        value={value ?? ''}
        withRequiredIcon={false}
        onBlur={onFieldBlur}
        onChange={onDataFieldChange}
      />
    );
  }

  if (valueType === CUSTOM_FIELDS_VALUE_TYPE.PHONE) {
    return (
      <DataFieldCDS
        dataTestId={dataTestId}
        disabled={disabled}
        error={hasError}
        helperText={errorMessage}
        isFieldChanged={isFieldChanged}
        isHidden={isHidden}
        label={labelNode}
        name={id}
        required={required}
        type={FieldInputType.TEL}
        value={value ?? ''}
        withRequiredIcon={false}
        onBlur={onFieldBlur}
        onChange={onDataFieldChange}
      />
    );
  }

  if (valueType === CUSTOM_FIELDS_VALUE_TYPE.NUMBER) {
    return (
      <TextFieldCDS
        dataTestId={dataTestId}
        disabled={disabled}
        error={hasError}
        helperText={errorMessage}
        isFieldChanged={isFieldChanged}
        isHidden={isHidden}
        label={labelNode}
        name={id}
        required={required}
        type={TEXT_FIELD_NUMBER_TYPE}
        value={value ?? ''}
        withRequiredIcon={false}
        onBlur={onFieldBlur}
        onChange={onTextChange}
      />
    );
  }

  return (
    <TextFieldCDS
      dataTestId={dataTestId}
      disabled={disabled}
      error={hasError}
      helperText={errorMessage}
      isFieldChanged={isFieldChanged}
      isHidden={isHidden}
      label={labelNode}
      name={id}
      required={required}
      value={value ?? ''}
      withRequiredIcon={false}
      onBlur={onFieldBlur}
      onChange={onTextChange}
    />
  );
};

CustomFieldInput.propTypes = {
  definition: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    valueType: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  errorKey: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  isHidden: PropTypes.bool,
  pendingChangesActionType: PropTypes.oneOf([...Object.values(PendingChangesActionType), null]),
  dataTestId: PropTypes.string,
  onBlur: PropTypes.func,
};

export default memo(CustomFieldInput);
